import { memo, SyntheticEvent, useEffect, useRef, useState } from "react";
import {
  Button,
  Dimmer,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Loader,
  Modal,
  Tab,
  TabPane,
  TabProps,
} from "semantic-ui-react";
import { useIntl } from "react-intl";

// Components - Queue
import CardServicesQueue from "react-lib/apps/MEH/CardServicesQueue";
import CardPatientQueue from "react-lib/apps/MEH/CardPatientQueue";
import CardMeReportList from "react-lib/apps/MEH/CardMeReportList";
import CardMePackageOrderSummary from "react-lib/apps/MEH/CardMePackageOrderSummary";

// Styles
import "./CardMeBillingMain.scss";

// serializer
import CentralLabOrderSimplifiedSerializer from "issara-sdk/types/CentralLabOrderSimplifiedSerializer_apps_LAB";
import CentralLabOrderItemActionLogSerializer from "issara-sdk/types/CentralLabOrderItemActionLogSerializer_apps_LAB";
import SpecimenContainerSerializer from "issara-sdk/types/SpecimenContainerSerializer_apps_LAB";
import VenipunctureNoteSerializer from "issara-sdk/types/VenipunctureNoteSerializer_apps_LAB"

// Components
import SnackMessage from "react-lib/apps/common/SnackMessage";
import CardMeBillingDetail from "react-lib/apps/MEH/CardMeBillingDetail";
import MeGenRunView from "react-lib/apps/MeGen/MeGenRunView";
import CardMeBillingCustomerList from "react-lib/apps/MEH/CardMeBillingCustomerList";
import ModMeBillingCustomer from "react-lib/apps/MEH/ModMeBillingCustomer";

// Interface
import { SaveServiceOrderType } from "react-lib/apps/MEH/sequence/SaveServiceOrder";
import { MedicalFeeHistoryType } from "react-lib/apps/HISV3/BIL/sequence/MedicalFeeHistory";
import { PaymentSummaryType } from "react-lib/apps/MEH/sequence/PaymentSummary";
import { PaymentHistoryType } from "react-lib/apps/MEH/sequence/PaymentHistory";
import { State as PackagePurchaseState } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";
import { State as PackageOrderState } from "react-lib/apps/HISV3/HCU/sequence/PackageOrder";
import { State as LabReportSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";
import {
  LabOrderQueueType,
  SpecimenCollectorMasterDataType,
} from "react-lib/apps/HISV3/LAB/LabInterface";
import { State as OPDLabSummarySequence } from "react-lib/apps/HISV3/LAB/sequence/OPDLabSummary";
import { State as MakeAppointmentMeHeyState } from "react-lib/apps/MEH/sequence/MakeAppointmentMeHey";
import { State as PatientQueueSequence } from "react-lib/apps/MEH/sequence/PatientQueue";
import { State as ReportListSequence } from "react-lib/apps/MEH/sequence/MeReportList"
import { State as SharedLabResultSequence } from "react-lib/apps/MEH/sequence/SharedLabResult";

// Types
import {
  RunSequence,
  SetProp,
  State as MeBillingState,
  OnEvent,
  PickedState,
  MAIN_CARD_NAME,
  GetCustomerListParams,
  CreateCustomerParams,
} from "./types/MeBillingType";
import { RowInfo } from "react-table-6";

type CardMeBillingMainProps = {
  controller: any;
  runSequence: RunSequence;
  setProp: SetProp;
  onEvent: OnEvent;
  MeBillingSequence: MeBillingState["MeBillingSequence"];
  SaveServiceOrderSequence?: SaveServiceOrderType;
  MedicalFeeHistorySequence?: MedicalFeeHistoryType;
  PaymentSummarySequence?: PaymentSummaryType;
  PaymentHistorySequence?: PaymentHistoryType;
  PackagePurchaseSequence?: PackagePurchaseState["PackagePurchaseSequence"];
  PackageOrderSequence?: PackageOrderState["PackageOrderSequence"];
  MakeAppointmentMeHeySequence?: MakeAppointmentMeHeyState["MakeAppointmentMeHeySequence"];
  MeGenSequence?: any;
  BillingAndPackageSequence?: any;
  goToMeGen?: { projectId: number; threadId?: string } | null;
  languageUX: "en" | "th";
  // Patient
  patientInfo?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  django?: Record<string, any>;

  // Invoice items
  invoiceItemByModes?: Record<string, any>[];
  invoiceItemByOrders?: Record<string, any>[];
  invoiceItemByItems?: Record<string, any>[];
  invoiceItemPackageList?: Record<string, any>[];

  // Orders
  patientOrders?: Record<string, any>[];
  refundOrders?: Record<string, any>;

  // Filters / Status
  medicalFeeFilter?: Record<string, any>;
  patientEncounterLockerStatus?: Record<string, any>[];
  encounterSuspension?: Record<string, any>;

  // Bill / Payment
  BillPaymentSequence?: Record<string, any> | null;
  billPendingDetail?: Record<string, any>;
  billReportPrint?: Record<string, any>;
  bilReceiptCodeDetail?: Record<string, any>;
  faxClaimItemList?: Record<string, any>;

  // Lab Order
  labOrderQueue?: LabOrderQueueType;
  selectedLabOrderWorking?: CentralLabOrderSimplifiedSerializer;
  specimenCollectorMasterData?: SpecimenCollectorMasterDataType;
  selectedContainer?: SpecimenContainerSerializer | null;
  specimenLogs?: CentralLabOrderItemActionLogSerializer[];
  selectedCentralLabBO?: Record<string, any>;

  // Lab Report
  LabReportSequence?: LabReportSequence["LabReportSequence"];
  permissionLabResultConfidential?: boolean;

  // Lab Summary
  OPDLabSummarySequence?: OPDLabSummarySequence["OPDLabSummarySequence"];
  selectedPatient?: Record<string, any>;
  centralLabResultList?: {
    items?: Record<string, any>[];
  };

  // Patient Queue
  PatientQueueSequence?: PatientQueueSequence["PatientQueueSequence"];

  // Master / Task
  masterOptions?: Record<string, any[]>;
  masterData?: Record<string, any[]>;
  taskTrackingList?: Record<string, any>;
  divisionType?: string;

  // Language
  billingPatientLanguage?: any;

  // Queue
  phlebotomyQueue?: Record<string, any>;
  meBillingActiveTab?: number;
  meBillingInitialMenu?: string;

  // print list
  printListController?: Record<string, any>;

  // scanner
  scannedDocumentController?: any;
  patientData?: Record<string, any>;

  // report list
  ReportListSequence?: ReportListSequence["ReportListSequence"]

  // receipt
  receiptId?: number | null;

  // shared lab result
  SharedLabResultSequence?: SharedLabResultSequence["SharedLabResultSequence"];

  // CardVenipunctureNote
  selectedVenipunctureNote?: VenipunctureNoteSerializer;

  // เลือกผู้รับบริการแล้วให้ภายนอกจัดการต่อแทนการเปิด CardMeBillingDetail
  onSelectCustomer?: () => void;
} & Pick<
  PickedState,
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "loadingStatus"
  | "masterOptions"
  | "selectedDivision"
  | "searchedItemListWithKey"
  | "qrCodePayment"
  | "userProfile"
>;

const CardMeBillingMain = (props: CardMeBillingMainProps) => {
  const intl = useIntl();

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [openCustomerMod, setOpenCustomerMod] = useState<boolean>(false);

  const isMountedRef = useRef(false);

  const selectedCustomerId = props.MeBillingSequence?.selectedCustomer?.patientDetail?.id;
  const showDetail = !!props.MeBillingSequence?.selectedCustomer && !props.onSelectCustomer;

  useEffect(()=> {
    // มีผู้รับบริการรอเปิดอยู่ (จากลิงก์ menu=MePOS)
    if (props.onSelectCustomer && props.meBillingInitialMenu && selectedCustomerId) {
      props.onSelectCustomer();
      return;
    }

    props.setProp("MeBillingSequence.selectedCustomer", undefined)
  }, [])

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    if (selectedCustomerId) {
      props.onSelectCustomer?.();
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    if (props.meBillingActiveTab !== undefined) {
      setActiveTabIndex(props.meBillingActiveTab);
    }
  }, [props.meBillingActiveTab]);

  useEffect(() => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_LIST",
    });
  }, [props.selectedDivision?.id]);

  const handleSearch = (filter: GetCustomerListParams) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_LIST",
      filter,
      activePage: 1,
    });
  };

  const handleChangePage = (activePage: number) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_LIST",
      activePage,
    });
  };

  const handleOpenNewCustomer = () => {
    setOpenCustomerMod(true);
  };

  const handleCloseCustomerMod = () => {
    setOpenCustomerMod(false);
  };

  const handleSaveCustomer = (data: CreateCustomerParams) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "CREATE_CUSTOMER",
      customerData: data,
      onSuccess: handleCloseCustomerMod,
    });
  };

  const handleCloseSnackMessage = () => {
    props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null);
    props.setProp(`successMessage.${MAIN_CARD_NAME}`, null);
  };

  const handleClearSelectCustomer = () => {
    props.setProp(`MeBillingSequence`, {
      ...props.MeBillingSequence,
      selectedCustomer: undefined,
    });

    handleSearch({});
  };

  const handleSelectCustomer = (selectedRow: RowInfo["original"]) => {
    if (!selectedRow?.id) {
      console.warn("[SelectedCustomer] Customer not found!!");
      return;
    }

    if (!selectedRow?.proxy_patient) {
      console.warn("[SelectedCustomer] Proxy Patient not found!!");
      return;
    }

    props.runSequence({
      sequence: "MeBilling",
      action: "GET_CUSTOMER_DETAIL",
      customerId: selectedRow.id,
      proxyPatientId: selectedRow.proxy_patient,
    });
  };

  // console.log("Yeti [MeBillingMain] Props: ", props);

  const handleChangeTab = (_e: SyntheticEvent, data: TabProps) => {
    setActiveTabIndex(data.activeIndex as number);
  };

  const panes = [
    {
      menuItem: {
        key: "search_member",
        content: (
          <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
            <label>Search Member</label>
          </div>
        ),
      },
      render: () => (
        <TabPane style={{ height: "100%", overflow: "auto", position: "relative" }}>
          <Dimmer active={props.loadingStatus?.[MAIN_CARD_NAME]} inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer>

          <SnackMessage
            error={props.errorMessage?.[MAIN_CARD_NAME]}
            success={props.successMessage?.[MAIN_CARD_NAME]}
            onClose={handleCloseSnackMessage}
          />

          <ModMeBillingCustomer
            open={openCustomerMod}
            onClose={handleCloseCustomerMod}
            onSave={handleSaveCustomer}
            setProp={props.setProp}
            initialData={null}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />

          {showDetail && (
            <CardMeBillingDetail
              controller={props.controller}
              selectedCustomer={props.MeBillingSequence?.selectedCustomer}
              visitList={props.MeBillingSequence?.visitList}
              selectedVisit={props.MeBillingSequence?.selectedVisit}
              appointmentList={props.MeBillingSequence?.appointmentList}
              packageDetail={props.MeBillingSequence?.packageDetail || []}
              providerList={props.MeBillingSequence?.common?.providerList || []}
              doctorList={props.MeBillingSequence?.common?.doctorList || []}
              onGoBack={handleClearSelectCustomer}
              initialMenu={props.meBillingInitialMenu}
              SaveServiceOrderSequence={props.SaveServiceOrderSequence}
              runSequence={props.runSequence}
              onEvent={props.onEvent as any}
              setProp={props.setProp}
              languageUX={props.languageUX}
              buttonLoadCheck={props.buttonLoadCheck}
              errorMessage={props.errorMessage}
              successMessage={props.successMessage}
              loadingStatus={props.loadingStatus}
              selectedDivision={props.selectedDivision}
              searchedItemListWithKey={props.searchedItemListWithKey}
              userProfile={props.userProfile}
              patientInfo={props.patientInfo}
              selectedEncounter={props.selectedEncounter}
              invoiceItemByModes={props.invoiceItemByModes}
              invoiceItemByOrders={props.invoiceItemByOrders}
              invoiceItemByItems={props.invoiceItemByItems}
              patientOrders={props.patientOrders}
              refundOrders={props.refundOrders}
              medicalFeeFilter={props.medicalFeeFilter}
              patientEncounterLockerStatus={props.patientEncounterLockerStatus}
              encounterSuspension={props.encounterSuspension}
              BillPaymentSequence={props.BillPaymentSequence}
              masterOptions={props.masterOptions}
              taskTrackingList={props.taskTrackingList}
              billPendingDetail={props.billPendingDetail}
              bilReceiptCodeDetail={props.bilReceiptCodeDetail}
              billReportPrint={props.billReportPrint}
              faxClaimItemList={props.faxClaimItemList}
              invoiceItemPackageList={props.invoiceItemPackageList}
              billingPatientLanguage={props.billingPatientLanguage}
              qrCodePayment={props.qrCodePayment}
              MedicalFeeHistorySequence={props.MedicalFeeHistorySequence}
              PaymentSummarySequence={props.PaymentSummarySequence}
              PaymentHistorySequence={props.PaymentHistorySequence}
              PackagePurchaseSequence={props.PackagePurchaseSequence}
              PackageOrderSequence={props.PackageOrderSequence}
              django={props.django}
              selectedLabOrderWorking={props.selectedLabOrderWorking}
              selectedCentralLabBO={props.selectedCentralLabBO}
              selectedContainer={props.selectedContainer}
              specimenLogs={props.specimenLogs}
              specimenCollectorMasterData={props.specimenCollectorMasterData}
              labOrderQueue={props.labOrderQueue}
              LabReportSequence={props.LabReportSequence}
              permissionLabResultConfidential={props.permissionLabResultConfidential}
              OPDLabSummarySequence={props.OPDLabSummarySequence}
              selectedPatient={props.selectedPatient}
              centralLabResultList={props.centralLabResultList}
              MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
              printListController={props.printListController}
              SharedLabResultSequence={props.SharedLabResultSequence}
              selectedVenipunctureNote={props.selectedVenipunctureNote}
            />
          )}

          {!showDetail && (
            <CardMeBillingCustomerList
              setProp={props.setProp}
              customerList={props.MeBillingSequence?.customerList}
              onSelectCustomer={handleSelectCustomer}
              onSearch={handleSearch}
              onPageChange={handleChangePage}
              onOpenNewCustomer={handleOpenNewCustomer}
              buttonLoadCheck={props.buttonLoadCheck}
              loadingStatus={props.loadingStatus}
              languageUX={props.languageUX}
            />
          )}
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: "services_queue",
        content: (
          <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
            <label>Services Queue</label>
          </div>
        ),
      },
      render: () => (
        <TabPane style={{ height: "100%", overflow: "hidden", padding: 0 }}>
          <CardServicesQueue onEvent={props.onEvent} phlebotomyQueue={props.phlebotomyQueue} />
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: "patient_queue",
        content: (
          <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
            <label>Patient Queue</label>
          </div>
        ),
      },
      render: () => (
        <TabPane style={{ height: "100%", overflow: "hidden", padding: 0 }}>
          <CardPatientQueue
            setProp={props.setProp}
            runSequence={props.runSequence}
            onEvent={props.onEvent}
            django={props.django}
            selectedEncounter={props.selectedEncounter}
            selectedPatient={props.selectedPatient}
            selectedDivision={props.selectedDivision}
            PatientQueueSequence={props.PatientQueueSequence}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            doctorList={props.MeBillingSequence?.common?.doctorList || []}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            // print list
            printListController={props.printListController}
            // scanner
            scannedDocumentController={props.scannedDocumentController}
            patientData={props.patientData}
            languageUX={props.languageUX}
          />
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: "report_list",
        content: (
          <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
            <label>Report</label>
          </div>
        ),
      },
      render: () => (
        <TabPane>
          <CardMeReportList
            setProp={props.setProp}
            runSequence={props.runSequence}
            onEvent={props.onEvent}
            ReportListSequence={props.ReportListSequence}
            selectedDivision={props.selectedDivision}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            languageUX={props.languageUX}
          />
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: "order_summary",
        content: (
          <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
            <label>{props.languageUX === "en" ? "Orders" : "รายการสั่งซื้อ"}</label>
          </div>
        ),
      },
      render: () => (
        <TabPane style={{ height: "100%", overflow: "auto", padding: 0 }}>
          <CardMePackageOrderSummary
            runSequence={props.runSequence}
            orderSummary={props.MeBillingSequence?.orderSummary}
            selectedDivision={props.selectedDivision}
            languageUX={props.languageUX}
          />
        </TabPane>
      ),
    },
  ];

  return (
    <div className="card-mebilling-main">
      <Tab
        activeIndex={activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={panes}
        onTabChange={handleChangeTab}
      />

      {/* จอ run ของ MeGen — เปิดจากปุ่มในใบผลแลป (LabReport: GOTO_MEGEN_PROJECT ตั้ง goToMeGen) */}
      <Modal
        open={!!props.goToMeGen}
        size="fullscreen"
        closeIcon
        onClose={() => props.setProp("goToMeGen", null)}
      >
        <Modal.Content style={{ height: "93vh", overflow: "auto", padding: 0 }}>
          {!!props.goToMeGen && (
            <MeGenRunView
              MeGenSequence={props.MeGenSequence}
              onEvent={props.onEvent}
              setProp={props.setProp}
              runSequence={props.runSequence}
              BillingAndPackageSequence={props.BillingAndPackageSequence}
              selectedDivisionId={props.selectedDivision?.id}
              defaultFinalOutput
              defaultAudioOnly={false}
              projectId={props.goToMeGen.projectId}
              threadId={props.goToMeGen.threadId || null}
              onNavigateBack={() => props.setProp("goToMeGen", null)}
            />
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};

CardMeBillingMain.displayName = "CardMeBillingMain";

export default memo(CardMeBillingMain);
